import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@/components/ui/button';
import {
  emailTemplateLibraryService,
  type TemplateCompare,
} from '@/services/email-template-library.service';

/**
 * Tenant vs platform diff. Side-by-side on desktop, STACKED on mobile — two code
 * panes cannot both be legible below `lg`, and horizontal scrolling of the page
 * is not permitted.
 */
export function CompareView({ templateKey }: { templateKey: string }) {
  const [data, setData] = useState<TemplateCompare | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    emailTemplateLibraryService
      .compare(templateKey)
      .then(setData)
      .catch((e: Error) => setError(e.message || 'The comparison could not be loaded.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [templateKey]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center">
        <p className="text-sm text-foreground">{error ?? 'No comparison available.'}</p>
        <Button variant="secondary" size="sm" className="mt-3" onClick={load}>
          Retry
        </Button>
      </div>
    );
  }

  const unchanged = !data.changed.subject && !data.changed.htmlBody && !data.changed.plainTextBody;

  return (
    <div className="space-y-3">
      {unchanged ? (
        <p className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
          Your version and the platform version are identical.
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          Differences between what your workspace sends (v{data.tenant.version}) and the current
          platform version (v{data.platform.version}).
        </p>
      )}

      {data.changed.subject && (
        <div className="rounded-lg border border-border">
          <p className="border-b border-border bg-muted/40 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Subject — modified
          </p>
          <div className="grid grid-cols-1 divide-y divide-border lg:grid-cols-2 lg:divide-x lg:divide-y-0">
            <div className="p-3">
              <p className="mb-1 text-[11px] font-medium text-muted-foreground">Yours</p>
              <p className="break-words text-sm text-foreground">{data.tenant.subject}</p>
            </div>
            <div className="p-3">
              <p className="mb-1 text-[11px] font-medium text-muted-foreground">Platform</p>
              <p className="break-words text-sm text-foreground">{data.platform.subject}</p>
            </div>
          </div>
        </div>
      )}

      {(data.variables.added.length > 0 || data.variables.removed.length > 0) && (
        <div className="rounded-lg border border-border p-3">
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Variables
          </p>
          {data.variables.added.length > 0 && (
            <p className="font-mono text-xs text-primary">+ {data.variables.added.join(', ')}</p>
          )}
          {data.variables.removed.length > 0 && (
            <p className="font-mono text-xs text-destructive">
              − {data.variables.removed.join(', ')}
            </p>
          )}
        </div>
      )}

      {data.changed.htmlBody && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <div className="min-w-0 rounded-lg border border-border">
            <p className="border-b border-border bg-muted/40 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Yours (v{data.tenant.version})
            </p>
            <pre className="max-h-80 overflow-auto p-3 font-mono text-[11px] leading-relaxed text-muted-foreground">
              {data.tenant.htmlBody}
            </pre>
          </div>
          <div className="min-w-0 rounded-lg border border-border">
            <p className="border-b border-border bg-muted/40 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Platform (v{data.platform.version})
            </p>
            <pre className="max-h-80 overflow-auto p-3 font-mono text-[11px] leading-relaxed text-muted-foreground">
              {data.platform.htmlBody}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
